import { useEffect } from 'react';

/**
 * Aggressive content protection hook.
 * - Blacks out all images when user switches away (to screenshot tool, etc.)
 * - Blocks PrintScreen key
 * - Hides images from print
 * - Blocks developer tools image inspection
 */
export function useContentProtection() {
  useEffect(() => {
    // 1. BLACK OUT images when user switches tabs/apps (catches screenshot tools)
    const handleVisibilityChange = () => {
      const imgs = document.querySelectorAll('.screenshot-guard img');
      if (document.hidden) {
        imgs.forEach(img => {
          img.style.filter = 'brightness(0)';
          img.style.transition = 'filter 0.01s';
        });
      } else {
        // Delay restore so screenshot tools that briefly unfocus don't capture
        setTimeout(() => {
          imgs.forEach(img => {
            img.style.filter = 'none';
          });
        }, 300);
      }
    };

    // 2. Block PrintScreen and common screenshot shortcuts
    const handleKeyDown = (e) => {
      const blocked = 
        e.key === 'PrintScreen' ||
        (e.ctrlKey && e.key === 'p') ||
        (e.ctrlKey && e.key === 's') ||
        (e.ctrlKey && e.shiftKey && (e.key === 'i' || e.key === 'I')) ||
        (e.ctrlKey && e.shiftKey && (e.key === 's' || e.key === 'S')) ||
        (e.metaKey && e.shiftKey && e.key === '3') || // Mac screenshot
        (e.metaKey && e.shiftKey && e.key === '4') || // Mac screenshot
        (e.metaKey && e.shiftKey && e.key === '5');    // Mac screenshot

      if (blocked) {
        e.preventDefault();
        e.stopPropagation();
        // Flash black on all protected images
        const imgs = document.querySelectorAll('.screenshot-guard img');
        imgs.forEach(img => {
          img.style.filter = 'brightness(0)';
          img.style.transition = 'filter 0.01s';
        });
        setTimeout(() => {
          imgs.forEach(img => {
            img.style.filter = 'none';
          });
        }, 1500);
        return false;
      }
    };

    // 3. Block window blur (catches Snipping Tool, ShareX, etc.)
    const handleBlur = () => {
      const imgs = document.querySelectorAll('.screenshot-guard img');
      imgs.forEach(img => {
        img.style.filter = 'brightness(0)';
        img.style.transition = 'filter 0.01s';
      });
    };

    const handleFocus = () => {
      setTimeout(() => {
        const imgs = document.querySelectorAll('.screenshot-guard img');
        imgs.forEach(img => {
          img.style.filter = 'none';
        });
      }, 300);
    };

    // 4. Block drag and drop of images
    const handleDragStart = (e) => {
      if (e.target.tagName === 'IMG') {
        e.preventDefault();
      }
    };

    // 5. Block right-click on the entire page with copyright message
    const handleContextMenu = (e) => {
      const isProtected = e.target.closest('.screenshot-guard') || e.target.tagName === 'IMG';
      if (isProtected) {
        e.preventDefault();
        alert('This artwork is protected by copyright. \u00a9 Scott McGuire / mortalworks.art');
        return false;
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('contextmenu', handleContextMenu);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('contextmenu', handleContextMenu);
    };
  }, []);
}
